/**
 * Reine Sichtbarkeitslogik des {@link NoteActionsOverlay}: **welche Blöcke zeigt das Menü, je
 * nachdem WOHIN geklickt wurde?**
 *
 * Es gibt zwei Geltungsbereiche ({@link ActionScope}):
 * - **`'note'`** – der Klick traf eine Note: Info-Zeile, Werkzeug-Launcher (Griff/Bass/
 *   Gesangstext), Noten-Edits (Teilen/Zusammenführen/Löschen) und – als abgesetzter letzter Block –
 *   die Takt-Editoren.
 * - **`'measure'`** – der Klick landete in der freien Taktfläche: ein Takt-Kopf („Takt 7") und
 *   **nur** die Takt-Editoren. Info-Zeile, Launcher und Noten-Edits gehören zu einer Note, die hier
 *   niemand angeklickt hat; sie würden auf die vom Takt-Watch automatisch nachgewählte Note wirken.
 *
 * **Der Geltungsbereich ist NICHT aus dem Auswahlzustand ableitbar** (kritisch – nicht dazu
 * rückbauen): `watch(selectedMeasureIndex)` in `osmd/useScoreSelection.ts` wählt nach jedem
 * Takt-Kontextwechsel selbst die erste Note des Takts aus. `selectedNote` ist danach also nie
 * `null`, und weil der Watch mit `flush: 'pre'` läuft, steht im Moment des synchronen Öffnens noch
 * die veraltete Note des Vortakts darin – eine Ableitung spränge sichtbar um. Der Bereich ist ein
 * Protokoll der letzten Zeigegeste und wird deshalb als eigener Ref geführt.
 *
 * Warum reine Funktion und nicht `v-if` im Template: das Repo hat kein `@vue/test-utils`,
 * Komponenten werden nie gemountet. Testbar ist nur, was hier liegt (siehe
 * `__tests__/noteActionsMenu.spec.ts`) – Vorbilder im selben Ordner sind {@link ./notePitchLabel}
 * und {@link ./measureEditEntries}.
 */
import type {NoteInfo} from '@/composables/musicXmlDom';
import type {MeasureEditEntry} from './measureEditEntries';

/** Wohin wurde zuletzt geklickt – auf eine Note oder in die freie Taktfläche? */
export type ActionScope = 'note' | 'measure';

export type NoteActionsBlockName =
    /** Tonhöhe der angeklickten Note (Kopf-Block, trägt eine eigene `border-b`). */
    | 'info'
    /** „Takt N" (Kopf-Block, trägt eine eigene `border-b`). */
    | 'measureHeader'
    /** Werkzeug-Launcher: Griff ändern / Bass bearbeiten / Gesangstext. */
    | 'tools'
    /** Teilen / Zusammenführen / Löschen. */
    | 'noteEdits'
    /** Die Takt-Editoren aus {@link measureEditEntries}. */
    | 'measureEdits';

export interface NoteActionsBlock {
    name: NoteActionsBlockName;
    /** Trennlinie **über** diesem Block. Nie über einem Kopf-Block (der bringt seine eigene mit)
     *  und nie über dem ersten Aktions-Block – so entsteht weder eine führende noch eine doppelte
     *  Linie, egal welche Kombination von Flags gerade gilt. */
    separator: boolean;
}

export interface NoteActionsMenuInput {
    scope: ActionScope;
    /** Freischaltungen der Vorschau (Props des Overlays). */
    showGrip: boolean;
    showBass: boolean;
    showLyric: boolean;
    /** `injectScoreEditContext(null) !== null` – ohne Kontext gibt es keine Noten-Edits. */
    hasEditContext: boolean;
    /** Ergebnis von {@link ./notePitchLabel!notePitchLabel} (`null` = Zeile ausblenden). */
    pitchLabel: string | null;
    /** Primäre Auswahl – nur für „Griff ändern" nötig (Spiegel des Toolbar-Gates `canEditGrip`). */
    selectedNote: NoteInfo | null | undefined;
    /** Gedruckte Nummer des Kontext-Takts (`selectedMeasure`), `null` = keiner. */
    measureNumber: number | null;
    /** `measureEditEntries({hasBassPreview})`, bereits auf `showMeasureEdits` gegatet. */
    measureEntries: readonly MeasureEditEntry[];
}

export interface NoteActionsMenu {
    /** Sichtbare Blöcke in Darstellungsreihenfolge (leer = nichts anzubieten). */
    blocks: NoteActionsBlock[];
    /** „Griff ändern" – eine **Pause** hat keinen Griff (Spiegel von `canEditGrip`). */
    showGripItem: boolean;
    showBassItem: boolean;
    showLyricItem: boolean;
    /** Beschriftung des Takt-Kopfs (`'Takt 7'`), `null` = kein Kopf. */
    measureHeader: string | null;
}

export function noteActionsMenu({
    scope,
    showGrip,
    showBass,
    showLyric,
    hasEditContext,
    pitchLabel,
    selectedNote,
    measureNumber,
    measureEntries,
}: NoteActionsMenuInput): NoteActionsMenu {
    const isNote = scope === 'note';

    const showGripItem = isNote && showGrip && !!selectedNote && !selectedNote.isRest;
    const showBassItem = isNote && showBass;
    const showLyricItem = isNote && showLyric;
    const measureHeader = !isNote && measureNumber !== null ? `Takt ${measureNumber}` : null;

    // [Name, sichtbar, ist Kopf-Block] – die Reihenfolge IST die Darstellungsreihenfolge.
    const order: readonly (readonly [NoteActionsBlockName, boolean, boolean])[] = [
        ['info', isNote && pitchLabel !== null, true],
        ['measureHeader', measureHeader !== null, true],
        ['tools', showGripItem || showBassItem || showLyricItem, false],
        ['noteEdits', isNote && hasEditContext, false],
        ['measureEdits', measureEntries.length > 0, false],
    ];

    const blocks: NoteActionsBlock[] = [];
    let sawAction = false;
    for (const [name, visible, isHeader] of order) {
        if (!visible) continue;
        blocks.push({name, separator: !isHeader && sawAction});
        if (!isHeader) sawAction = true;
    }

    return {blocks, showGripItem, showBassItem, showLyricItem, measureHeader};
}

/** Lohnt sich das Menü in diesem Geltungsbereich überhaupt?
 *
 * Im Takt-Bereich besteht es **ausschließlich** aus den Takt-Editoren. Auf der
 * Griffschrift-Ausgabe sind die über `showMeasureEdits` (= `showAdvancedTools`) abgeschaltet – die
 * Karte wäre dort ein reiner Kopf ohne Eintrag. Der Host öffnet sie dann gar nicht erst: der
 * Rechtsklick behält sein natives Browser-Menü, der Doppelklick öffnet nichts. */
export function actionsMenuHasContent(scope: ActionScope, showMeasureEdits: boolean): boolean {
    return scope === 'note' || showMeasureEdits;
}
